import React, { useState, useEffect } from "react";
import { Grid, Typography, Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Snackbar, Alert } from "@mui/material";
import axios from "axios";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import StarIcon from "@mui/icons-material/Star";
import "./style.css";

export default function PauseOrPayJob(props) {
  const [jobDetails, setJobDetails] = useState({
    title: "",
    description: "",
    jobType: "",
    jobCategory: "",
    location: "",
    emergency: false,
    recurring: false,
    scheduledTime: "",
    jobId: "",
    owner: {
      ratings: 0,
      reviews: ""
    }
  });
  const [jobImage, setJobImage] = useState(null);
  const [showFullImage, setShowFullImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // New state for confirmation dialogs and success messages
  const [openPayDialog, setOpenPayDialog] = useState(false);
  const [openPauseDialog, setOpenPauseDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [openSuccessAlert, setOpenSuccessAlert] = useState(false);

  const navigate = useNavigate();
  const { jobId } = useParams();

  // Fetch job details when the component mounts
  useEffect(() => {
    if (jobId) {
      fetchJobDetails(jobId);
      fetchJobImage(jobId);
    } else {
      setError("No job ID in the URL.");
      setLoading(false);
    }
  }, [jobId]);

  // Function to fetch job details
  const fetchJobDetails = async (id) => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.data) {
        setJobDetails(response.data);
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
      setError("Failed to load job details. " + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch job image
  const fetchJobImage = async (id) => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.get(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${id}/image`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob' // Important for image data
      });
      
      if (response.data) {
        // Create a URL for the blob data
        const imageUrl = URL.createObjectURL(response.data);
        setJobImage(imageUrl);
      }
    } catch (err) {
      console.error("Error fetching job image:", err);
      // We don't set error state here to avoid blocking the whole component if only the image fails
    }
  };

  // Event handler for "View Image" button
  const handleViewImage = () => {
    if (jobImage) {
      setShowFullImage(!showFullImage);
    } else {
      fetchJobImage(jobId);
    }
  };

  // Format the scheduledTime to display in a readable format
  const formatDate = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("D MMM YYYY");
  };

  const formatTime = (dateTime) => {
    if (!dateTime) return "";
    return moment(dateTime).format("LT");
  };

  // Event handler for "View Invoice" button
  const handleViewInvoice = () => {
    if (jobId) {
      navigate(`/InvoicePage/${jobId}`);
    } else {
      console.log("Job ID is missing.");
    }
  };
  
  // New handlers for Pay and Pause buttons
  const handleOpenPayDialog = () => {
    setOpenPayDialog(true);
  };

  const handleOpenPauseDialog = () => {
    setOpenPauseDialog(true);
  };

  const handleClosePayDialog = () => {
    setOpenPayDialog(false);
  };

  const handleClosePauseDialog = () => {
    setOpenPauseDialog(false);
  };

  const handleCloseSuccessAlert = () => {
    setOpenSuccessAlert(false);
  };

  // Function to handle job payment
  const handlePayJob = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.put(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/pay`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.status === 200) {
        setSuccessMessage("Job has been paid successfully!");
        setOpenSuccessAlert(true);
        // Refresh job details
        fetchJobDetails(jobId);
      }
    } catch (err) {
      console.error("Error paying for job:", err);
      setError("Failed to pay for job. " + (err.response?.data?.message || err.message));
    } finally {
      handleClosePayDialog();
    }
  };

  // Function to handle job pausing
  const handlePauseJob = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      
      const response = await axios.put(` https://jobmanagement-497524552274.us-central1.run.app/api/jobs/${jobId}/pause`, {}, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });
      
      if (response.status === 200) {
        setSuccessMessage("Job has been paused successfully!");
        setOpenSuccessAlert(true);
        // Refresh job details
        fetchJobDetails(jobId);
      }
    } catch (err) {
      console.error("Error pausing job:", err);
      setError("Failed to pause job. " + (err.response?.data?.message || err.message));
    } finally {
      handleClosePauseDialog();
    }
  };

  if (loading) {
    return <Typography>Loading job details...</Typography>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Grid container padding="3%">
      <Grid container>
        <Grid item xs={12} sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ mr: 2 }}>
            Job Details
          </Typography>
          <Typography variant="h4" sx={{ color: "text.secondary" }}>
            - {jobId}
          </Typography>
        </Grid>
        
        {/* Basic Job Details Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelDivCls">
              <div className="labelCls">Job Title</div>
              <div className="valueCls">{jobDetails.title}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Type</div>
              <div className="valueCls">{jobDetails.jobType}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Category</div>
              <div className="valueCls">{jobDetails.jobCategory}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Description</div>
              <div className="valueCls">{jobDetails.description}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Location</div>
              <div className="valueCls">{jobDetails.location}</div>
            </div>
          </Grid>
        </Grid>

        {/* Emergency and Scheduling Details Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Grid>
            <div className="labelDivCls">
              <div className="labelCls">Is this an emergency?</div>
              <div className="valueCls">{jobDetails.emergency ? "Yes" : "No"}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Job Date</div>
              <div className="valueCls">{formatDate(jobDetails.scheduledTime)}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Selected Time</div>
              <div className="valueCls">{formatTime(jobDetails.scheduledTime)}</div>
            </div>
            
            <div className="labelDivCls">
              <div className="labelCls">Is this a recurring issue?</div>
              <div className="valueCls">{jobDetails.recurring ? "Yes" : "No"}</div>
            </div>
          </Grid>
        </Grid>

        {/* Homeowner Rating and Reviews Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Homeowner Information
          </Typography>
          
          <div className="labelDivCls">
            <div className="labelCls">Homeowner Rating</div>
            <div className="valueCls">
              <StarIcon style={{ fontSize: 18, color: "#0d4f57", verticalAlign: "middle" }} />
              {jobDetails.owner?.ratings || 0}
            </div>
          </div>
          
          <div className="labelDivCls">
            <div className="labelCls">Homeowner Reviews</div>
            <div className="valueCls">{jobDetails.owner?.reviews || "No reviews available"}</div>
          </div>
        </Grid>

        {/* Job Image Section */}
        <Grid item xs={12} sx={{ p: 2 }} className="main-box-viewJobs-card">
          <Typography variant="h6" sx={{ mb: 2 }}>
            Job Images by HomeOwner
          </Typography>
          
          {jobImage && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <img 
                src={jobImage} 
                alt="Job Image" 
                style={{ 
                  maxWidth: showFullImage ? '100%' : '300px', 
                  maxHeight: showFullImage ? 'auto' : '200px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => setShowFullImage(!showFullImage)}
              />
              {showFullImage && (
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                  Click the image to resize
                </Typography>
              )}
            </Box>
          )}
          
          {!jobImage && (
            <Typography>No image available for this job</Typography>
          )}
        </Grid>

        {/* Buttons for job actions */}
        <Grid item xs={12} sx={{ p: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenPayDialog}
            sx={{ mr: 2 }}
          >
            PAY JOB
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleOpenPauseDialog}
          >
            PAUSE JOB
          </Button>
          {/* <Button
            variant="contained"
            color="info"
            onClick={handleViewInvoice}
            sx={{ ml: 2 }}
          >
            View Invoice
          </Button> */}
        </Grid>
      </Grid>

      {/* Pay Confirmation Dialog */}
      <Dialog
        open={openPayDialog}
        onClose={handleClosePayDialog}
        aria-labelledby="pay-dialog-title"
        aria-describedby="pay-dialog-description"
      >
        <DialogTitle id="pay-dialog-title">Confirm Payment</DialogTitle>
        <DialogContent>
          <DialogContentText id="pay-dialog-description">
            Are you sure you want to pay for this job? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePayDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handlePayJob} color="primary" variant="contained" autoFocus>
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pause Confirmation Dialog */}
      <Dialog
        open={openPauseDialog}
        onClose={handleClosePauseDialog}
        aria-labelledby="pause-dialog-title"
        aria-describedby="pause-dialog-description"
      >
        <DialogTitle id="pause-dialog-title">Confirm Pause</DialogTitle>
        <DialogContent>
          <DialogContentText id="pause-dialog-description">
            Are you sure you want to pause this job? This will temporarily halt all progress.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePauseDialog} color="primary">
            Cancel
          </Button>
          <Button onClick={handlePauseJob} color="primary" variant="contained" autoFocus>
            Confirm Pause
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Alert */}
      <Snackbar 
        open={openSuccessAlert} 
        autoHideDuration={6000} 
        onClose={handleCloseSuccessAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSuccessAlert} 
          severity="success" 
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
}